/**
 * lib/statementPdf.ts
 * ------------------------------------------------------------------
 * A4 family statement of account, payslip house style. Shared by the
 * bursar's Statements screen and the parent portal's Fees tab so both
 * print an identical document.
 * ---------------------------------------------------------------- */
import { jsPDF } from 'jspdf';
import { SCHOOL } from '@/lib/school.config';
import { fmtPula, type FamilyStatement } from '@/lib/financeCore';

export function buildStatementPdf(s: FamilyStatement, logo: string | null): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 16; const right = 210 - MX;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 16;

  if (logo) { try { pdf.addImage(logo, 'PNG', MX, y - 2, 18, 18); } catch { /* ignore */ } }
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text(SCHOOL.name, MX + 22, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}  ·  ${SCHOOL.contact.website}`, MX + 22, y + 9);
  y += 22;
  setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 8;

  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
  pdf.text('STATEMENT OF ACCOUNT', MX, y);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(`As at ${new Date().toISOString().slice(0, 10)}`, right, y, { align: 'right' });
  y += 7;

  pdf.setFontSize(7.5); setT(MUTED); pdf.text('TO', MX, y);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); setT(INK);
  pdf.text(`${s.contactName || s.familyName}`, MX, y + 4.5);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(MUTED);
  pdf.text(`${s.familyName}${s.contactEmail ? `  ·  ${s.contactEmail}` : ''}`, MX, y + 9);
  y += 16;

  const cell = (txt: string, x: number, align: 'left' | 'right' = 'left') => pdf.text(txt, x, y, { align });
  setF(NAVY); pdf.rect(MX, y - 4.5, right - MX, 7, 'F');
  pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5);
  cell('STUDENT', MX + 2); cell('Billed', right - 78, 'right'); cell('Paid', right - 40, 'right'); cell('Balance', right - 2, 'right');
  y += 6;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  for (const m of s.members) {
    setT(INK); cell(m.name, MX + 2);
    setT(MUTED); cell(fmtPula(m.billed), right - 78, 'right'); cell(fmtPula(m.paid), right - 40, 'right');
    setT(INK); cell(fmtPula(m.balance), right - 2, 'right');
    y += 6;
  }
  y += 2;

  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.text('Account activity', MX, y); y += 2;
  setD(RULE); pdf.setLineWidth(0.3); pdf.line(MX, y, right, y); y += 5;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
  const rows = s.txns.slice(-28);
  if (rows.length < s.txns.length) { setT(MUTED); pdf.text(`(showing the most recent ${rows.length} of ${s.txns.length} entries)`, MX, y); y += 5; }
  for (const t of rows) {
    if (y > 250) { pdf.setFontSize(8); setT(MUTED); pdf.text('… continued — see online account', MX, y); y += 5; break; }
    setT(MUTED); pdf.text((t.date || '').slice(0, 10), MX, y);
    setT(INK); pdf.text(pdf.splitTextToSize(t.description, 95)[0] as string, MX + 24, y);
    if (t.charge) { setT(INK); pdf.text(fmtPula(t.charge), right - 40, y, { align: 'right' }); }
    if (t.credit) { setT([5, 150, 105]); pdf.text(fmtPula(t.credit), right - 40, y, { align: 'right' }); }
    setT(INK); pdf.text(fmtPula(t.balance), right - 2, y, { align: 'right' });
    y += 5.2;
  }
  y += 2;

  // ---- forward-looking payment schedule (what's due when, what's overdue) ----
  if (s.schedule && s.schedule.length > 0) {
    setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); pdf.text('Payment schedule', MX, y); y += 2;
    setD(RULE); pdf.setLineWidth(0.3); pdf.line(MX, y, right, y); y += 5;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
    for (const r of s.schedule) {
      if (y > 250) { setT(MUTED); pdf.text('… see your online account for the full schedule', MX, y); y += 5; break; }
      const overdue = r.status === 'overdue';
      setT(overdue ? [190, 18, 60] : MUTED); pdf.text((r.dueDate || '').slice(0, 10), MX, y);
      setT(INK); pdf.text(pdf.splitTextToSize(`${r.studentName} — ${r.label}`, 90)[0] as string, MX + 24, y);
      if (overdue) { setT([190, 18, 60]); pdf.setFont('helvetica', 'bold'); pdf.text('OVERDUE', right - 42, y, { align: 'right' }); pdf.setFont('helvetica', 'normal'); }
      setT(INK); pdf.text(fmtPula(r.balance), right - 2, y, { align: 'right' });
      y += 5.2;
    }
    y += 2;
  }

  setF([240, 244, 250]); setD(NAVY); pdf.setLineWidth(0.5);
  pdf.rect(MX, y, right - MX, 13, 'FD');
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
  pdf.text('TOTAL AMOUNT DUE', MX + 3, y + 8.4);
  pdf.setFontSize(13);
  pdf.text(fmtPula(s.balance), right - 3, y + 8.6, { align: 'right' });
  y += 19;

  setT(MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
  const ageStr = [`Current ${fmtPula(s.aging.current)}`, ...s.aging.buckets.map((b) => `${b.label} ${fmtPula(b.amount)}`)].join('   ·   ');
  pdf.text(pdf.splitTextToSize(`Aging:  ${ageStr}`, right - MX) as string[], MX, y);
  y += 10;

  pdf.setFontSize(7.5);
  pdf.text(pdf.splitTextToSize('Please settle the total amount due. Payment is by bank transfer (EFT) or card. Quote the family name as the payment reference. Thank you.', right - MX) as string[], MX, y);

  return pdf;
}
